/* AthleteBrandBadges
 * ─────────────────────────────────────────────────────────────────
 * Renders every brand pill an athlete is tagged into. Reads
 * `athlete.brand_tags` and resolves each slug against the active
 * brand_networks list (via `useBrandNetworks`).
 *
 * Variant `pill` stacks pills along the bottom-right of the photo
 * (matches the original HopeBadge placement). Each subsequent badge
 * slides left by PILL_STEP_PX so multi-brand athletes read left→right.
 *
 * For pages that prefer inline placement (next to a name), pass
 * `variant="inline"`.
 */
import useBrandNetworks from '../hooks/useBrandNetworks';
import BrandBadge from './BrandBadge';

const PILL_STEP_PX = 64;   // horizontal offset between stacked pills

const AthleteBrandBadges = ({
  athlete,
  variant = 'pill',
  size = 'md',
  exclude = [],
  /* For pill variant only: starting `right` offset (px) for the first
     badge. Bump this when there's a verified-check dot occupying the
     bottom-right corner so the brand pill sits cleanly to its left. */
  offsetRight = 4,
  /* Optional vertical offset class (e.g. "-bottom-1.5") so callers can
     match other badges in the same stack. */
  bottomClass = '-bottom-1',
}) => {
  const { bySlug } = useBrandNetworks();
  if (!athlete) return null;
  const tags = Array.isArray(athlete.brand_tags) ? athlete.brand_tags : [];
  const visible = tags
    .filter((slug) => !exclude.includes(slug))
    .map((slug) => bySlug[slug])
    .filter(Boolean);
  if (visible.length === 0) return null;

  if (variant === 'inline') {
    return (
      <span className="inline-flex items-center gap-1 flex-wrap">
        {visible.map((b) => (
          <BrandBadge key={b.slug} brand={b} variant="inline" size={size} />
        ))}
      </span>
    );
  }

  return (
    <>
      {visible.map((b, i) => (
        <span
          key={b.slug}
          className={`absolute ${bottomClass} z-10`}
          style={{ right: `${offsetRight + i * PILL_STEP_PX}px` }}
        >
          <BrandBadge brand={b} variant="inline" size={size} />
        </span>
      ))}
    </>
  );
};

export default AthleteBrandBadges;
